import duration, { Duration } from 'dayjs/plugin/duration'
import dayjs, { Dayjs } from 'dayjs'
import {
    AccountRecord,
    AdsOrOrganicFilterView,
    ContentSearchParams,
    ContentSearchParamsView,
    DmFilter,
    InputMaybe,
    ReadFilterView,
    SocialMediaServiceView
} from '../../../api/client'
import { ClassifierSearchParam } from '../../../models/ClassifierSearchParam'
import { AdOrOrganicFilterOption } from '../../AdOrOrganicComboBox'

dayjs.extend(duration)

type SearchParameters = {
    // Mandatory
    keyword: string
    moderation: string
    selectedAccountIds: number[]
    selectedClassifiers: ClassifierSearchParam[]
    selectedGeneralClassifiers: string[]
    selectedSocialMediaServices: SocialMediaServiceView[]
    selectedTeams: number[]
    excludeOwnComments: boolean
    dmFilter: DmFilter

    // Optional
    account?: AccountRecord
    adOrOrganic?: AdOrOrganicFilterOption
    contentFeedId?: number
    contentIds?: number[]
    // Comments collected under these posts only (admin Posts page).
    postIds?: number[]
    authorProfileIds?: number[]
    endDateTime?: Dayjs
    owned?: boolean
    readReceipt?: ReadFilterView
    startDateTime?: Dayjs
    timeDuration?: Duration
    selectedReplies?: string
    selectedSuggestedReplies?: string
    hasMedia?: boolean
}
export default SearchParameters

export function mapAdOrOrganicFilterOption(
    option?: AdOrOrganicFilterOption
): AdsOrOrganicFilterView | undefined {
    switch (option) {
        case AdOrOrganicFilterOption.Ads:
            return AdsOrOrganicFilterView.Ads
        case AdOrOrganicFilterOption.Organic:
            return AdsOrOrganicFilterView.Organic
        case AdOrOrganicFilterOption.Both:
            return AdsOrOrganicFilterView.Both
        default:
            return undefined
    }
}

export function mapAdOrOrganicFilterView(
    option?: AdsOrOrganicFilterView
): AdOrOrganicFilterOption | undefined {
    switch (option) {
        case AdsOrOrganicFilterView.Ads:
            return AdOrOrganicFilterOption.Ads
        case AdsOrOrganicFilterView.Organic:
            return AdOrOrganicFilterOption.Organic
        case AdsOrOrganicFilterView.Both:
            return AdOrOrganicFilterOption.Both
        default:
            return undefined
    }
}

export function mapSearchParametersFromView(
    searchParameters: ContentSearchParamsView,
    allSocialMediaServices: SocialMediaServiceView[]
): SearchParameters {
    let timeDuration = searchParameters.timeDuration
        ? dayjs.duration(searchParameters.timeDuration)
        : undefined

    // We have a few errors to handle with this mapping!
    const selectedSocialMediaServices = (searchParameters.socialMediaServiceIds ?? []).map(
        (id: number) => {
            if (typeof id !== 'number' || isNaN(id)) {
                throw new Error(`Invalid ID found in socialMediaServiceIds: ${id}`)
            }

            const matchedService = allSocialMediaServices.find((service) => service.id === id)

            if (!matchedService) {
                throw new Error(`Service with ID ${id} not found`)
            }

            return matchedService
        }
    )

    const selectedClassifiers: ClassifierSearchParam[] = (
        searchParameters.classifierSearchParams ?? []
    ).map((classifier) => {
        return {
            classifierName: classifier.classifierName,
            threshold: classifier.threshold
        } as ClassifierSearchParam
    })
    let adOrOrganic = mapAdOrOrganicFilterView(searchParameters.adsOrOrganic ?? undefined)
    // The GraphQL model returns an array of strings, but that's _never_ what we want. So
    // we're going to take the first one, and default to 'all' if there's nothing there.
    let moderation = searchParameters.moderationString?.[0] ?? 'all'

    let selectedGeneralClassifiers =
        searchParameters.classifierSearchParams?.map((c) => c.classifierName) ?? []

    // The nullish coalescing in the mappings below ( `... ?? undefined`) are to make sure
    // we're not dealing with `null`s, only with `undefined`s or some default value (`[]`, `''`)
    return {
        adOrOrganic,
        contentIds: searchParameters.contentIds ?? undefined,
        postIds: searchParameters.postIds ?? undefined,
        endDateTime: searchParameters.before ? dayjs(searchParameters.before) : undefined,
        keyword: searchParameters.containsText ?? '',
        moderation,
        owned: searchParameters.owned ?? undefined,
        readReceipt: searchParameters.readFilter ?? undefined,
        selectedAccountIds: searchParameters.accountIds ?? [],
        authorProfileIds: searchParameters.authorProfileIds ?? [],
        selectedClassifiers,
        selectedSocialMediaServices,
        selectedTeams: searchParameters.teamIds ?? [],
        startDateTime: searchParameters.since ? dayjs(searchParameters.since) : undefined,
        timeDuration,
        selectedGeneralClassifiers,
        excludeOwnComments: searchParameters.excludeOwnComments ? true : false,
        dmFilter: searchParameters.dmFilter,

        hasMedia: searchParameters.hasMedia ?? undefined

        // NOT MAPPING
        // account, // Not searching by individual account -- see instead selectedAccountIds
    }
}

export function mapToContentSearchParams(searchParameters: SearchParameters): ContentSearchParams {
    let classifierSearchParams = searchParameters.selectedGeneralClassifiers.map((c) => {
        return {
            classifierName: c
        }
    })

    return {
        accountIds: searchParameters.selectedAccountIds,
        adsOrOrganic: searchParameters.adOrOrganic as
            | InputMaybe<AdsOrOrganicFilterView>
            | undefined,
        before: searchParameters.endDateTime,
        authorProfileIds: searchParameters.authorProfileIds || [],
        classifierSearchParams,
        containsText: searchParameters.keyword,
        contentIds: searchParameters.contentIds,
        postIds: searchParameters.postIds,
        moderationString: [searchParameters.moderation],
        owned: searchParameters.owned,
        readFilter: searchParameters.readReceipt,
        since: searchParameters.startDateTime,
        socialMediaServiceIds: searchParameters.selectedSocialMediaServices.map(
            (smsService) => smsService.id
        ),
        teamIds: searchParameters.selectedTeams,
        timeDuration: searchParameters.timeDuration?.toISOString(),
        timeOffset: undefined,
        excludeOwnComments: searchParameters.excludeOwnComments,
        dmFilter: searchParameters.dmFilter,
        hasMedia: searchParameters.hasMedia
    }
}

/**
 * The filters exactly as ContentList sends them to the content search. Not the same
 * as mapToContentSearchParams above (which saves a feed): this one takes the
 * thresholded `selectedClassifiers` and lower-cases the moderation. The post page's
 * Threaded tab uses it too, so its "N comments filtered" counts agree with the
 * Chronological list under the same filters.
 */
export function mapToContentListSearchParams(
    searchParameters: SearchParameters,
    paging?: { maxResults?: number; skipResults?: number }
): ContentSearchParams {
    return {
        timeDuration: searchParameters.timeDuration
            ? `${searchParameters.timeDuration.toISOString()}`
            : undefined,
        accountIds: searchParameters.selectedAccountIds,
        authorProfileIds: searchParameters.authorProfileIds || [],
        teamIds: searchParameters.selectedTeams,
        moderationString: [searchParameters.moderation?.toLowerCase()],
        since: searchParameters.startDateTime,
        before: searchParameters.endDateTime,
        socialMediaServiceIds: searchParameters.selectedSocialMediaServices.map(
            (service) => service.id
        ),
        containsText: searchParameters.keyword,
        classifierSearchParams: searchParameters.selectedClassifiers,
        adsOrOrganic: searchParameters.adOrOrganic as
            | InputMaybe<AdsOrOrganicFilterView>
            | undefined,
        contentIds: searchParameters.contentIds,
        postIds: searchParameters.postIds,
        owned: searchParameters.owned,
        readFilter: searchParameters.readReceipt,
        excludeOwnComments: searchParameters.excludeOwnComments,
        dmFilter: searchParameters.dmFilter,
        hasMedia: searchParameters.hasMedia,
        maxResults: paging?.maxResults,
        skipResults: paging?.skipResults
    }
}
